import isFunction from 'lodash/isFunction';
import React, { Component } from 'react';

import LivingLensService, { ERROR_CODES } from '@services/LivingLens';

// # ----------------------------------------------------------------------
// # TYPES
// # ----------------------------------------------------------------------

export type ComponentReadyPayload = {
  userId: string;
  sessionId: string;
  warnings: string[];
  errors: string[];
};

export interface LivingLensV1Props {
  apiKey: string;
  channelId: string;
  respondentId: string;
  questionId: string;
  mediaTypes: 'video' | 'audio' | 'image'[];
  mediaCountryCode: string;
  mediaLanguageCode: string;
  mediaMinLength?: string;
  mediaMaxLength?: string;
  interfaceLanguageCode: string;
  maxWidth?: string;
  singleUseMode?: boolean;
  disableBranding?: boolean;
  widgetDomain?: string;
  widgetEnvironment?: string;
  region?: string;
  onUploadSuccess: (...args: unknown[]) => unknown;
  onRecordComplete: (...args: unknown[]) => unknown;
  onRecordReset: (...args: unknown[]) => unknown;
  onReadyToRecord: (...args: unknown[]) => unknown;
  onError: (...args: unknown[]) => unknown;
  //  https://jira.medallia.com/browse/OA-30777
  onComponentReady: (payload: ComponentReadyPayload) => void;
  // https://jira.medallia.com/browse/OA-31511
  dataAllowUploads?: boolean;
  dataAllowRecording?: boolean;
  dataPreferredCamera?: 'user' | 'environment';
}

// # ----------------------------------------------------------------------
// # COMPONENT
// # ----------------------------------------------------------------------

export default class LivingLensV1 extends Component<LivingLensV1Props> {
  static defaultProps = {
    mediaMinLength: '',
    mediaMaxLength: '',
    dataAllowUploads: false,
    dataPreferredCamera: '',
    dataAllowRecording: false,
    maxWidth: '700',
    singleUseMode: true,
    disableBranding: true,
    region: null
  };

  constructor(props: LivingLensV1Props) {
    super(props);

    this.livingLensContainer = `multimedia-${this.props.questionId}`;
    this.LivingLensService = new LivingLensService(this.props.widgetDomain, this.handleErrors);
  }

  mediaCaptureCallbacks = (mediaCapture) => {
    const {
      onComponentReady,
      onUploadSuccess,
      onRecordComplete,
      onRecordReset,
      onReadyToRecord,
      onError
    } = this.props;

    mediaCapture.onEvent('componentReady', onComponentReady);
    mediaCapture.onEvent('uploadSuccessful', onUploadSuccess);
    mediaCapture.onEvent('recordingStopped', onRecordComplete);
    mediaCapture.onEvent('stateHasReset', onRecordReset);
    mediaCapture.onEvent('readyToRecord', onReadyToRecord);
    // Errors
    mediaCapture.onEvent('uploadFailed', () => onError(ERROR_CODES.UPLOAD_FAILED));
    mediaCapture.onEvent('stalledUploadDetected', () => onError(ERROR_CODES.UPLOAD_STALLED));
    mediaCapture.onEvent('recordingTimeout', () => onError(ERROR_CODES.RECORDING_TIMEOUT));
    mediaCapture.onEvent('appendFiltersFailed', () => onError(ERROR_CODES.APPEND_FILTERS_FAILED));
    mediaCapture.onEvent('noAudioCaptureDetected', () => onError(ERROR_CODES.NO_RECORDING_DEVICE));
    mediaCapture.onEvent('deviceNotSupported', () => onError(ERROR_CODES.NO_RECORDING_DEVICE));
    mediaCapture.onEvent('accessDenied', () => onError(ERROR_CODES.NO_RECORDING_DEVICE));
    mediaCapture.onEvent('noVideoCaptureDeviceDetected', () =>
      onError(ERROR_CODES.NO_RECORDING_DEVICE)
    );
    // Generic widget error event - forward code and message
    // TODO: Confirm event payload with the LivingLens team
    mediaCapture.onEvent('error', (err) => onError(ERROR_CODES.GENERIC_WIDGET_ERROR, err));
  };

  handleErrors = (errorCode) => {
    this.props.onError(errorCode);
  };

  componentDidMount() {
    const LivingLens = this.LivingLensService.getInstance();
    if (isFunction(LivingLens.startMedalliaMediaCapture)) {
      LivingLens.startMedalliaMediaCapture({
        target: this.livingLensContainer,
        callback: this.mediaCaptureCallbacks,
        filters: [
          {
            group: 'mec_ballot_id',
            filters: [this.props.respondentId]
          },
          {
            group: 'mec_question_id',
            filters: [this.props.questionId]
          }
        ],
        environment: this.props.widgetEnvironment
      });
    } else {
      this.props.onError(ERROR_CODES.WIDGET_VERSION_NOT_AVAILABLE);
    }
  }

  shouldComponentUpdate() {
    return false;
  }

  render() {
    const {
      apiKey,
      channelId,
      respondentId,
      questionId,
      mediaTypes,
      mediaCountryCode,
      mediaLanguageCode,
      mediaMinLength,
      mediaMaxLength,
      interfaceLanguageCode,
      maxWidth,
      dataAllowUploads,
      dataPreferredCamera,
      dataAllowRecording,
      singleUseMode,
      disableBranding,
      widgetEnvironment,
      region
    } = this.props;

    const reloadOnComplete = singleUseMode ? {} : { 'data-reload-on-complete': true };
    const dataRegion = region ? { 'data-channel-region': region } : {};

    return (
      <div
        data-livinglens-container={this.livingLensContainer}
        data-api-key={apiKey}
        data-channel-id={channelId}
        data-respondent-id={respondentId}
        data-question-id={questionId}
        data-capture-types={mediaTypes.join(',')}
        data-media-min-length={mediaMinLength}
        data-media-max-length={mediaMaxLength}
        data-country-code={mediaCountryCode}
        data-language-code={mediaLanguageCode}
        data-interface-language-code={interfaceLanguageCode}
        data-size-max-width={maxWidth}
        data-prefered-camera={dataPreferredCamera}
        data-allow-uploads={dataAllowUploads}
        data-allow-recording={dataAllowRecording}
        data-disable-branding={disableBranding}
        data-channel-environment={widgetEnvironment}
        {...dataRegion}
        {...reloadOnComplete}
      />
    );
  }
}
